import { message, OwnerSelectDropdown } from '@repo/ui';
import { type CamelToSnakeCaseKeys } from '@repo/utils';
import {
  Button, Card, Flex, Form, Input,
  InputNumber, Radio, Select, Typography,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import {
  useCreateWebhookMutation,
  useListWebhooksQuery,
  useUpdateWebhookMutation,
} from 'crm-atoms/services/webhookService';
import { type CreateWebhookPayload, type WebhookType } from 'crm-atoms/types/webhook';
import { useCallback, useEffect, useState } from 'react';

import CustomHeaderUI from './CustomHeadersUi';
import styles from './ModifyWebhookDrawer.module.scss';

interface ModifyWebhookDrawerProps {
  mode: 'create' | 'edit';
  onClose?: () => void;
  onFinish: (webhook: WebhookType) => void;
  webhookId?: string | number;
}

const httpOptions = [
  { value: 'https', label: 'https://' },
  { value: 'http', label: 'http://' },
];

function FormItemTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography.Text
      strong
      style={{ width: '12rem', textAlign: 'start' }}
    >
      {children}
    </Typography.Text>
  );
}

export default function ModifyWebhookDrawer({
  mode,
  onClose,
  onFinish,
  webhookId,
}: ModifyWebhookDrawerProps) {
  const [loading, setLoading] = useState(false);
  const [form] = useForm();
  const [createWebhookTrigger] = useCreateWebhookMutation();
  const [updateWebhookTrigger] = useUpdateWebhookMutation();

  const {
    data: webhookData,
  } = useListWebhooksQuery(webhookId!, {
    skip: !webhookId || mode !== 'edit',
  });

  useEffect(() => {
    if (webhookData?.webhooks.length) {
      const data = webhookData.webhooks[0];
      const formData = {
        ...data,
        contentType: data?.meta?.headers?.['Content-Type'],
        url: data?.meta?.path,
        protocol: data?.meta?.protocol || 'https',
        headers: Object.entries(data?.meta?.headers || {})
          .filter(([key]) => key !== 'Content-Type')
          .map(([key, value]) => ({
            key,
            value,
          })),
      };
      form.setFieldsValue(formData);
    }
  }, [form, webhookData?.webhooks, webhookId]);

  const handleFinish = useCallback(async (values: any) => {
    setLoading(true);

    try {
      const createWebhookPayload: CamelToSnakeCaseKeys<CreateWebhookPayload> = {
        name: values.name,
        meta: {
          protocol: values.protocol,
          headers: {
            'Content-Type': values.contentType,
            ...(values.headers?.reduce((acc: Record<string, string>, header: { key: string; value: string }) => ({
              ...acc,
              [header.key]: header.value,
            }), {} as Record<string, string>) || {}),
          },
          path: values.url,
        },
        max_retries: values.maxRetries,
        save_response: values.saveResponse,
        notify_on_failure: values.notifyOnFailure,
      };

      let response;
      if (mode === 'create') {
        response = await createWebhookTrigger(createWebhookPayload);
      } else {
        response = await updateWebhookTrigger({ id: webhookId!, payload: createWebhookPayload });
      }

      if ('error' in response) throw response.error;
      if (response.data?.success) {
        onFinish(response.data.webhook);
        onClose?.();
      }
    } catch (error: any) {
      message.error(error?.data?.message ?? 'Something went wrong saving webhook');
    } finally {
      setLoading(false);
    }
  }, [createWebhookTrigger, mode, onFinish, updateWebhookTrigger, webhookId, onClose]);

  return (
    <div className={styles.container}>
      <Card title={mode === 'create' ? 'Create Webhook' : 'Edit Webhook'}>
        <Form
          form={form}
          onFinish={handleFinish}
          layout="horizontal"
          preserve={false}
          colon={false}
          clearOnDestroy
        >
          <Flex vertical>
            <Form.Item
              name="name"
              label={<FormItemTitle>Name</FormItemTitle>}
              rules={[{ required: true, message: 'Please enter webhook name' }]}
              required={false}
            >
              <Input placeholder="Enter Webhook Name" />
            </Form.Item>
            <Form.Item
              name="url"
              label={<FormItemTitle>URL</FormItemTitle>}
              rules={[{ required: true, message: 'Please enter webhook URL' }]}
              required={false}
            >
              <Input
                addonBefore={(
                  <Form.Item name="protocol" noStyle initialValue="https">
                    <Select options={httpOptions} />
                  </Form.Item>
                )}
                placeholder="Enter Webhook URL"
              />
            </Form.Item>
            <Form.Item
              name="contentType"
              label={<FormItemTitle>Content Type</FormItemTitle>}
              initialValue="application/json"
              rules={[{ required: true }]}
              required={false}
            >
              <Select className={styles.contentSelect}>
                <Select.Option value="application/json">application/json</Select.Option>
                <Select.Option value="application/x-www-form-urlencoded">
                  application/x-www-form-urlencoded
                </Select.Option>
                <Select.Option value="text/plain">text/plain</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="saveResponse"
              label={<FormItemTitle>Save Response</FormItemTitle>}
              initialValue={false}
            >
              <Radio.Group>
                <Radio.Button value>Yes</Radio.Button>
                <Radio.Button value={false}>No</Radio.Button>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              name="headers"
              label={<FormItemTitle>Custom Headers</FormItemTitle>}
            >
              <CustomHeaderUI />
            </Form.Item>
            <Form.Item
              name="notifyOnFailure"
              label={<FormItemTitle>Notify on Failure</FormItemTitle>}
            >
              <OwnerSelectDropdown
                multiple
                forceFetch
                handleClick={(val) => form.setFieldValue('notifyOnFailure', val)}
                selectProps={{
                  placeholder: 'Search for sales users',
                }}
              />
            </Form.Item>
            <Form.Item
              name="maxRetries"
              label={<FormItemTitle>Retry Count</FormItemTitle>}
            >
              <InputNumber min={0} placeholder="Enter Retry Count for Webhook" />
            </Form.Item>
          </Flex>
        </Form>
      </Card>

      <Flex justify="flex-end">
        <Button type="primary" onClick={form.submit} loading={loading} disabled={loading}>
          {mode === 'create' ? 'Create' : 'Save'}
        </Button>
      </Flex>
    </div>
  );
}
